import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowRightIcon,
  CheckCircle2Icon,
  CheckIcon,
  ChevronRightIcon,
  HistoryIcon,
  MegaphoneIcon,
  XIcon,
} from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  newestPublishedAt,
  partitionBySeen,
  releaseNotesQueryOptions,
  type ReleaseNote,
} from '@/lib/api/release-notes.js';

import { CATEGORY_STYLE, NoteMarkdown, relativeDate } from './note-content.js';
import { getLastSeenPublishedAt, markSeen } from './seen-store.js';

import './whats-new-archive.css';

export interface WhatsNewPanelProps {
  /** Close the popover (used by the header X and the "All updates" link). */
  onClose: () => void;
}

/**
 * One feed entry, per the approved artifact drawer: category badge + unread
 * dot + relative date row, then title and markdown body. Unread entries get
 * a primary left bar.
 */
function Entry({
  note,
  fresh,
}: {
  note: ReleaseNote;
  fresh: boolean;
}): JSX.Element {
  const style = note.category ? CATEGORY_STYLE[note.category] : undefined;
  return (
    <article className={fresh ? 'wn-entry unread' : 'wn-entry'}>
      <div className="wn-erow">
        {style && (
          <span className={`wn-badge c-${note.category}`}>{style.label}</span>
        )}
        {fresh ? (
          <span className="wn-udot" role="img" aria-label="Unread" />
        ) : null}
        <span className="wn-when">{relativeDate(note.publishedAt)}</span>
      </div>
      <h3 className="wn-etitle">{note.title}</h3>
      <div className="wn-body">
        <NoteMarkdown bodyMd={note.bodyMd} />
      </div>
    </article>
  );
}

/**
 * "What's new" — the release-notes panel shown in a Popover anchored to the
 * header megaphone (a floating card, no backdrop). Mounts on open: it snapshots
 * the last-seen boundary at mount, lists notes published since then under
 * "New for you", then a "You're all caught up" divider, then earlier notes —
 * matching the approved artifact drawer. Marking the newest note seen clears
 * the header dot.
 */
export function WhatsNewPanel({ onClose }: WhatsNewPanelProps): JSX.Element {
  const { data: notes, isLoading } = useQuery(releaseNotesQueryOptions());
  const newest = newestPublishedAt(notes);

  const [seenAtOpen] = useState<string | null>(() => getLastSeenPublishedAt());
  const [markedAll, setMarkedAll] = useState(false);
  const [showPrevious, setShowPrevious] = useState(false);

  useEffect(() => {
    if (newest !== null) markSeen(newest);
  }, [newest]);

  /**
   * The layout partitions against the boundary snapshotted at open — marking
   * all read only clears the header dot and hides the button; the notes the
   * user is looking at must not collapse into the caught-up state mid-open.
   */
  const { fresh, previous } = partitionBySeen(notes, seenAtOpen);
  const hasNotes = (notes?.length ?? 0) > 0;

  const markAllRead = () => {
    if (newest !== null) {
      markSeen(newest);
      setMarkedAll(true);
    }
  };

  return (
    <div className="wn-drawer flex max-h-[min(38rem,calc(100dvh-5rem))] flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <MegaphoneIcon className="size-4 text-primary" aria-hidden />
        <h2 className="text-base font-semibold text-foreground">
          What&apos;s new
        </h2>
        <div className="ml-auto flex items-center gap-1">
          {fresh.length > 0 && newest !== null && !markedAll && (
            <button
              type="button"
              onClick={markAllRead}
              className="rounded px-1.5 py-1 text-xs font-medium text-primary hover:bg-muted"
            >
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto pb-2 [scrollbar-gutter:stable]">
        {isLoading && (
          <p className="px-4 py-4 text-sm text-muted-foreground">Loading…</p>
        )}

        {!isLoading && !hasNotes && (
          <div className="flex items-start gap-3 px-4 py-5">
            <CheckCircle2Icon
              className="mt-0.5 size-6 shrink-0 text-[var(--wn-new)]"
              aria-hidden
            />
            <div>
              <p className="text-sm font-semibold text-foreground">
                No updates yet
              </p>
              <p className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                Project news and updates will appear here.
              </p>
            </div>
          </div>
        )}

        {!isLoading && fresh.length > 0 && (
          <>
            <p className="wn-sectlabel">New for you</p>
            {fresh.map((note) => (
              <Entry key={note.id} note={note} fresh={!markedAll} />
            ))}
            <div className="wn-caughtup">
              <CheckIcon strokeWidth={2.4} aria-hidden />
              You&rsquo;re all caught up
            </div>
            {previous.map((note) => (
              <Entry key={note.id} note={note} fresh={false} />
            ))}
          </>
        )}

        {!isLoading && hasNotes && fresh.length === 0 && (
          <>
            <div className="flex items-start gap-3 px-5 py-5">
              <CheckCircle2Icon
                className="mt-0.5 size-6 shrink-0 text-[var(--wn-new)]"
                aria-hidden
              />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  You&rsquo;re all caught up!
                </p>
                <p className="mt-0.5 text-[0.8125rem] text-muted-foreground">
                  No new updates right now
                </p>
              </div>
            </div>
            {previous.length > 0 && (
              <>
                <button
                  type="button"
                  aria-expanded={showPrevious}
                  aria-controls="wn-previous-updates"
                  onClick={() => setShowPrevious((open) => !open)}
                  className="flex w-full items-center gap-3 border-t border-border px-5 py-3.5 text-left transition-colors hover:bg-muted/60"
                >
                  <HistoryIcon
                    className="size-4.5 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="text-sm font-medium text-foreground">
                    See previous updates
                  </span>
                  <ChevronRightIcon
                    className={`ml-auto size-4.5 text-muted-foreground transition-transform ${showPrevious ? 'rotate-90' : ''}`}
                    aria-hidden
                  />
                </button>
                <div
                  id="wn-previous-updates"
                  hidden={!showPrevious}
                  className="border-t border-border"
                >
                  {showPrevious &&
                    previous.map((note) => (
                      <Entry key={note.id} note={note} fresh={false} />
                    ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="border-t border-border p-3">
        <Button
          asChild
          variant="outline"
          className="w-full justify-center rounded-full"
        >
          <Link to="/whats-new" onClick={onClose}>
            All updates
            <ArrowRightIcon className="size-4" aria-hidden />
          </Link>
        </Button>
      </div>
    </div>
  );
}
